import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { api, type User } from "../lib/api";
import { LoadingButton, InlineLoading } from "./Spinner";
import PasswordInput from "./PasswordInput";
import Avatar from "./Avatar";

interface Props {
  open: boolean;
  onClose: () => void;
}

// Modal version of the former /account page. Same cards, same handlers
// — just wrapped in a centered overlay so users can edit their profile
// without leaving the request they're working on. Mirrors the shape of
// WorkspaceSettingsModal so the two feel consistent.
export default function AccountModal({ open, onClose }: Props) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const me = useQuery({ queryKey: ["me"], queryFn: () => api.me(), enabled: open });
  const user = me.data?.user ?? null;
  const invitesQ = useQuery({ queryKey: ["my-invites"], queryFn: () => api.myInvites(), enabled: open });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !user) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4 bg-[color:var(--backdrop)]"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-3xl h-[min(820px,90vh)] overflow-hidden flex flex-col shadow-[var(--shadow-strong)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-base font-semibold">Account</h2>
            <span className="text-xs muted-strong truncate">— {user.name ?? user.email}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
          >×</button>
        </div>

        <div className="flex-1 min-h-0 overflow-auto scroll-pane p-5 space-y-5 bg-bg">
          <ProfileCard user={user} onSaved={() => qc.invalidateQueries({ queryKey: ["me"] })} />
          <PasswordCard hasPassword={user.hasPassword} />
          <MfaCard onChanged={() => qc.invalidateQueries({ queryKey: ["me"] })} />
          <PendingInvitesCard
            invites={invitesQ.data?.invites ?? []}
            onChanged={() => {
              qc.invalidateQueries({ queryKey: ["my-invites"] });
              qc.invalidateQueries({ queryKey: ["workspaces"] });
            }}
          />
          <div className="card p-5">
            <LogoutButton onSignOut={() => { onClose(); nav("/login"); }} />
          </div>
          <DeleteAccountCard email={user.email} onDeleted={() => { onClose(); nav("/login"); }} />
        </div>
      </div>
    </div>
  );
}

function ProfileCard({ user, onSaved }: { user: User; onSaved: () => void }) {
  const [name, setName] = useState(user.name ?? "");
  const save = useMutation({
    mutationFn: () => api.updateMe({ name }),
    onSuccess: onSaved,
  });

  const fileRef = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: (file: File) => api.avatars.uploadMe(file),
    onSuccess: onSaved,
  });
  const removeAvatar = useMutation({
    mutationFn: () => api.avatars.removeMe(),
    onSuccess: onSaved,
  });

  const src = user.avatarUpdatedAt ? api.avatars.userUrl(user.id, user.avatarUpdatedAt) : null;

  return (
    <form
      className="card p-5 space-y-4"
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
    >
      <h3 className="font-semibold">Profile</h3>
      <div className="flex items-center gap-4 flex-wrap">
        <Avatar src={src} name={user.name} email={user.email} size={64} />
        <div className="flex flex-col gap-2 min-w-0">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) upload.mutate(f);
              if (fileRef.current) fileRef.current.value = "";
            }}
            className="hidden"
          />
          <div className="flex gap-2 flex-wrap">
            <LoadingButton type="button" className="btn" pending={upload.isPending} onClick={() => fileRef.current?.click()}>
              Upload photo
            </LoadingButton>
            {user.avatarUpdatedAt && (
              <LoadingButton type="button" className="btn-danger" pending={removeAvatar.isPending} onClick={() => removeAvatar.mutate()}>
                Remove
              </LoadingButton>
            )}
          </div>
          <p className="text-xs muted-strong">PNG, JPG, GIF, WebP, or SVG. Up to 5 MB.</p>
          {upload.isError && <p className="text-xs text-danger">{String((upload.error as Error).message)}</p>}
        </div>
      </div>
      <div className="space-y-2">
        <label className="label">Email</label>
        <input className="input" value={user.email} disabled />
      </div>
      <div className="space-y-2">
        <label className="label">Display name</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
      </div>
      <div className="flex justify-end">
        <LoadingButton type="submit" className="btn-primary" pending={save.isPending} pendingLabel="Saving…">
          Save
        </LoadingButton>
      </div>
    </form>
  );
}

function PasswordCard({ hasPassword }: { hasPassword: boolean }) {
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const save = useMutation({
    mutationFn: () => api.updatePassword({ currentPassword: current || undefined, password: pw }),
    onSuccess: () => { setDone(true); setCurrent(""); setPw(""); },
    onError: (e) => setErr(String((e as Error).message ?? "Couldn't update")),
  });
  return (
    <form
      className="card p-5 space-y-3"
      onSubmit={(e) => { e.preventDefault(); setErr(null); setDone(false); save.mutate(); }}
    >
      <h3 className="font-semibold">{hasPassword ? "Change password" : "Set a password"}</h3>
      {hasPassword && (
        <div className="space-y-2">
          <label className="label">Current password</label>
          <PasswordInput value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
        </div>
      )}
      <div className="space-y-2">
        <label className="label">New password</label>
        <PasswordInput minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" />
      </div>
      {err && <p className="text-xs text-danger">{err}</p>}
      {done && <p className="text-xs text-good">Updated.</p>}
      <div className="flex justify-end">
        <LoadingButton type="submit" className="btn-primary" pending={save.isPending} pendingLabel="Saving…">
          {hasPassword ? "Update password" : "Set password"}
        </LoadingButton>
      </div>
    </form>
  );
}

function PendingInvitesCard({ invites, onChanged }: { invites: { id: string; workspace_name: string; inviter_name: string | null; inviter_email: string }[]; onChanged: () => void }) {
  if (!invites.length) return null;
  return (
    <div className="card p-5 space-y-3">
      <h3 className="font-semibold">Pending invites</h3>
      <ul className="space-y-2">
        {invites.map((i) => <InviteRow key={i.id} invite={i} onChanged={onChanged} />)}
      </ul>
    </div>
  );
}

function InviteRow({ invite, onChanged }: { invite: { id: string; workspace_name: string; inviter_name: string | null; inviter_email: string }; onChanged: () => void }) {
  const accept = useMutation({ mutationFn: () => api.acceptInvite(invite.id), onSuccess: onChanged });
  const reject = useMutation({ mutationFn: () => api.rejectInvite(invite.id), onSuccess: onChanged });
  return (
    <li className="flex items-center justify-between gap-3 rounded-md border border-border bg-surface-2 p-3">
      <div className="text-sm">
        <div className="font-medium">{invite.workspace_name}</div>
        <div className="muted-strong text-xs">From {invite.inviter_name ?? invite.inviter_email}</div>
      </div>
      <div className="flex gap-2">
        <LoadingButton className="btn" pending={reject.isPending} onClick={() => reject.mutate()}>Decline</LoadingButton>
        <LoadingButton className="btn-primary" pending={accept.isPending} onClick={() => accept.mutate()}>Accept</LoadingButton>
      </div>
    </li>
  );
}

function LogoutButton({ onSignOut }: { onSignOut: () => void }) {
  const qc = useQueryClient();
  const logout = useMutation({
    mutationFn: () => api.logout(),
    onSuccess: () => {
      qc.setQueryData(["me"], { user: null });
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
      onSignOut();
    },
  });
  return (
    <LoadingButton className="btn-danger" pending={logout.isPending} onClick={() => logout.mutate()} pendingLabel="Signing out…">
      Sign out
    </LoadingButton>
  );
}

// Irreversible account deletion. Requires the user to type their email to
// confirm — matching what the server checks. Workspaces they solely own are
// deleted; shared ones are handed to a remaining member.
function DeleteAccountCard({ email, onDeleted }: { email: string; onDeleted: () => void }) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const del = useMutation({
    mutationFn: () => api.account.remove(confirm.trim()),
    onSuccess: () => {
      qc.setQueryData(["me"], { user: null });
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== "me" });
      onDeleted();
    },
    onError: (e) => {
      const m = String((e as Error).message ?? "");
      setErr(m.includes("confirm_email_mismatch") ? "The email you typed doesn't match your account." : "Couldn't delete the account.");
    },
  });
  const matches = confirm.trim().toLowerCase() === email.toLowerCase();
  return (
    <div className="card p-5 space-y-3 border-danger/40">
      <h3 className="font-semibold text-danger">Delete account</h3>
      <p className="text-sm muted">
        Permanently deletes your account and the workspaces you solely own. Workspaces shared with others are transferred
        to a remaining member. This cannot be undone — consider exporting your data first.
      </p>
      <div className="space-y-2">
        <label className="label">Type <span className="font-mono">{email}</span> to confirm</label>
        <input
          className="input"
          value={confirm}
          onChange={(e) => { setConfirm(e.target.value); setErr(null); }}
          placeholder={email}
          autoComplete="off"
        />
      </div>
      {err && <p className="text-xs text-danger">{err}</p>}
      <div>
        <LoadingButton
          className="btn-danger"
          pending={del.isPending}
          disabled={!matches}
          pendingLabel="Deleting…"
          onClick={() => { setErr(null); del.mutate(); }}
        >
          Delete my account
        </LoadingButton>
      </div>
    </div>
  );
}

function MfaCard({ onChanged }: { onChanged: () => void }) {
  const status = useQuery({ queryKey: ["mfa", "me"], queryFn: () => api.mfa.status() });
  const [enrolling, setEnrolling] = useState<{ secret: string; otpauth: string } | null>(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const start = useMutation({
    mutationFn: () => api.mfa.start(),
    onSuccess: (r) => { setEnrolling(r); setCode(""); setErr(null); },
  });
  const confirm = useMutation({
    mutationFn: () => api.mfa.confirm(code.trim()),
    onSuccess: (r) => { setRecoveryCodes(r.recoveryCodes); setEnrolling(null); status.refetch(); onChanged(); },
    onError: (e) => setErr(parseMfaError(e)),
  });
  const disable = useMutation({
    mutationFn: () => api.mfa.disable(code.trim()),
    onSuccess: () => { setRecoveryCodes(null); setCode(""); status.refetch(); onChanged(); },
    onError: (e) => setErr(parseMfaError(e)),
  });

  if (!status.data) return <div className="card p-5"><InlineLoading /></div>;

  return (
    <div className="card p-5 space-y-3">
      <h3 className="font-semibold">Two-factor authentication</h3>
      <p className="text-sm muted">
        Adds an extra step to sign-in using an authenticator app (Google Authenticator, 1Password, Authy, Bitwarden, …).
      </p>

      {!status.data.enabled && !enrolling && (
        <LoadingButton className="btn-primary" pending={start.isPending} onClick={() => start.mutate()}>
          Set up two-factor
        </LoadingButton>
      )}

      {enrolling && (
        <div className="rounded-md border border-border bg-surface-2 p-4 space-y-4">
          <p className="text-sm">Scan this code with your authenticator app, or enter the secret manually.</p>
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="bg-white p-3 rounded">
              <QRCodeSVG value={enrolling.otpauth} size={160} level="M" />
            </div>
            <div className="flex-1 space-y-2 min-w-0">
              <div>
                <label className="label">Secret</label>
                <pre className="code-block break-all">{enrolling.secret}</pre>
              </div>
              <div>
                <label className="label">Enter the 6-digit code from your app</label>
                <input
                  className="input input-mono mt-1"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123 456"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                />
              </div>
              {err && <p className="text-xs text-danger">{err}</p>}
              <div className="flex gap-2 justify-end">
                <button className="btn" onClick={() => { setEnrolling(null); setErr(null); }}>Cancel</button>
                <LoadingButton className="btn-primary" pending={confirm.isPending} disabled={code.trim().length < 6} onClick={() => confirm.mutate()}>
                  Confirm + enable
                </LoadingButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {status.data.enabled && !enrolling && (
        <div className="rounded-md border border-good/40 bg-good/10 p-3 text-sm space-y-3">
          <p>✓ Two-factor is enabled. Sign-in requires a code from your authenticator.</p>
          <div className="space-y-2">
            <label className="label">Enter a code (or recovery code) to disable</label>
            <div className="flex gap-2">
              <input
                className="input input-mono"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123 456 or xxxx-xxxx"
                inputMode="text"
              />
              <LoadingButton className="btn-danger" pending={disable.isPending} disabled={!code.trim()} onClick={() => disable.mutate()}>
                Disable
              </LoadingButton>
            </div>
            {err && <p className="text-xs text-danger">{err}</p>}
          </div>
        </div>
      )}

      {recoveryCodes && (
        <div className="rounded-md border border-warn bg-warn/10 p-3 text-sm space-y-2">
          <p className="font-semibold">Save your recovery codes</p>
          <p className="muted">
            Each code works once if you lose access to your authenticator. They won't be shown again.
          </p>
          <pre className="code-block">{recoveryCodes.join("\n")}</pre>
          <button className="btn" onClick={() => setRecoveryCodes(null)}>I've saved them</button>
        </div>
      )}
    </div>
  );
}

function parseMfaError(e: unknown): string {
  const m = String((e as Error).message ?? "");
  if (m.includes("bad_code") || m.includes("bad_mfa_code")) return "That code didn't match — try the next one your authenticator shows.";
  if (m.includes("no_pending")) return "Restart enrollment — the pending secret was discarded.";
  return "Something went wrong.";
}
